import { Component, computed, inject, input } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonInput, IonInputPasswordToggle, IonTextarea } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { addIcons } from 'ionicons';
import { eyeOffOutline, eyeOutline } from 'ionicons/icons';
import { keys } from 'lodash-es';

@Component({
  selector: 'mtjr-text-field',
  imports: [ReactiveFormsModule, IonInput, IonInput, IonInputPasswordToggle],
  template: `
    <ion-input
      [label]="fullLabel()"
      [formControl]="field()"
      [class]="class() + ' text-dark'"
      mode="md"
      [fill]="fill()"
      [labelPlacement]="labelPlacement()"
      [type]="type()"
      [placeholder]="placeholder()"
      [errorText]="getErrorText()"
      [inputmode]="inputmode()"
      (keydown)="blockInvalid($event)"
      (ionInput)="filterInvalid($event)"
      enterkeyhint="next"
    >
      @if (type() === 'password') {
        <ion-input-password-toggle slot="end" />
      } @else {
        <ng-content></ng-content>
      }
    </ion-input>
  `,
})
export class VxTextFieldComponent {
  field = input.required<FormControl>();
  required = computed(() => this.field().hasValidator(Validators.required));
  label = input<string>();
  fullLabel = computed(() => {
    const label = this.label();
    if (!label) return '';
    return label + (this.required() ? ' *' : '');
  });
  name = input<string>('');
  placeholder = input<string>('');

  mode = input('md');
  fill = input('outline');
  class = input<string>('');
  type = input<
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
  >('text');

  inputmode = input<string>('');
  blockInvalid(ev: KeyboardEvent) {
    // block if not number (allow modifier keys for shortcuts like paste)
    if (this.inputmode() === 'decimal' && !/^[0-9]*$/.test(ev.key) && !ev.metaKey && !ev.ctrlKey) {
      ev.preventDefault();
    }
  }

  filterInvalid(ev: Event) {
    if (this.inputmode() !== 'decimal') return;
    const input = ev.target as HTMLInputElement;
    const filtered = input.value.replace(/[^0-9]/g, '');
    if (input.value !== filtered) {
      input.value = filtered;
      this.field().setValue(filtered);
    }
  }

  translate = inject(TranslateService);

  errorMessages = input<Record<string, string>>({});

  labelPlacement = computed(() => {
    const label = this.label();
    return label && label.length > 1 ? 'floating' : '';
  });

  constructor() {
    addIcons({ eyeOutline, eyeOffOutline });
  }

  getErrorText() {
    const field = this.field();
    if (!field.dirty) return null;
    if (field.hasError('required')) {
      return this.errorMessages()['required'] ?? this.translate.instant('VALIDATION.REQUIRED');
    } else if (field.hasError('email')) {
      return this.errorMessages()['email'] ?? this.translate.instant('VALIDATION.EMAIL');
    } else if (field.hasError('minlength')) {
      const requiredLength = field.errors?.['minlength']?.requiredLength;
      return (
        this.errorMessages()['minlength'] ?? this.translate.instant('VALIDATION.MIN_LENGTH', { min: requiredLength })
      );
    } else if (field.hasError('match_error')) {
      return this.errorMessages()['match_error'] ?? this.translate.instant('VALIDATION.PASSWORD_MISMATCH');
    } else if (field.hasError('min')) {
      const min = field.errors?.['min']?.min;
      return this.errorMessages()['min'] ?? this.translate.instant('VALIDATION.MIN', { min });
    } else if (field.hasError('max')) {
      const max = field.errors?.['max']?.max;
      return this.errorMessages()['max'] ?? this.translate.instant('VALIDATION.MAX', { max });
    } else if (field.errors) {
      const code = keys(field.errors)?.[0];
      return this.errorMessages()[code] ?? this.translate.instant('VALIDATION.INVALID_INPUT');
    } else {
      return null;
    }
  }
}

@Component({
  selector: 'mtjr-text-area-field',
  imports: [ReactiveFormsModule, IonTextarea],
  template: `
    <ion-textarea
      [label]="fullLabel()"
      label-placement="floating"
      [formControl]="field()"
      [autoGrow]="true"
      [class]="class()"
      mode="md"
      [readonly]="field().disabled"
      [fill]="fill()"
      [placeholder]="placeholder()"
      [required]="field().hasError('required')"
      [errorText]="getErrorText()"
      [rows]="rows()"
    />
  `,
})
export class VxTextAreaFieldComponent extends VxTextFieldComponent {
  rows = input(3);
  constructor() {
    super();
  }
}
